import { Injectable } from '@angular/core'
import { FactorEnum } from '@modules/stocks-module/orders/enums/factor.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { SlTpValuesEnum } from '@modules/stocks-module/orders/enums/sl-tp-values.enum'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { OrderClassEnum } from '@modules/orders-module/enums/order-class.enum'
import { OrderStatusEnum } from '@modules/orders-module/enums/order-status.enum'
import { PositionSideEnum } from '@modules/positions-module/enums/position-side.enum'
import { OrderAttributeEnum } from '@modules/orders-module/enums/order-attribute.enum'
import { CancelOrderStatuses } from '@modules/orders-module/constants/cancel-order-statuses'
import { FilledOrderStatuses } from '@modules/orders-module/constants/filled-order-statuses'
import { OpenOrderStatuses } from '@modules/orders-module/constants/open-order-statuses'
import { OrderClasses } from '@modules/orders-module/constants/order-classes'
import { BehaviorSubject } from 'rxjs'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { SlTpValuesInterface } from '@modules/stocks-module/orders/interfaces/sl-tp-values.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { TrailOrderSocketResponseInterface } from '@modules/stocks-module/orders/interfaces/trail-order-socket-response.interface'

@Injectable({
	providedIn: 'root',
})
export class StocksOrdersHelperService {
	protected readonly map: MapConstructor = Map
	protected readonly factorEnum: typeof FactorEnum = FactorEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly slTpValuesEnum: typeof SlTpValuesEnum = SlTpValuesEnum
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly orderClassEnum: typeof OrderClassEnum = OrderClassEnum
	protected readonly orderStatusEnum: typeof OrderStatusEnum = OrderStatusEnum
	protected readonly positionSideEnum: typeof PositionSideEnum = PositionSideEnum
	protected readonly orderAttributeEnum: typeof OrderAttributeEnum = OrderAttributeEnum
	protected readonly cancelOrderStatuses: OrderStatusEnum[] = CancelOrderStatuses
	protected readonly filledOrderStatuses: OrderStatusEnum[] = FilledOrderStatuses
	protected readonly openedOrderStatuses: OrderStatusEnum[] = OpenOrderStatuses
	protected readonly orderClasses: OrderClassEnum[] = OrderClasses

	public ordersResponseMap: Map<OrderStateEnum, BehaviorSubject<OrdersResponseInterface | null>> = new this.map<
		OrderStateEnum,
		BehaviorSubject<OrdersResponseInterface | null>
	>([
		[this.orderStateEnum.OPEN, new BehaviorSubject<OrdersResponseInterface | null>(null)],
		[this.orderStateEnum.CANCELED, new BehaviorSubject<OrdersResponseInterface | null>(null)],
		[this.orderStateEnum.FILLED, new BehaviorSubject<OrdersResponseInterface | null>(null)],
	])

	public ordersLoadingMap: Map<OrderStateEnum, BehaviorSubject<boolean>> = new this.map<
		OrderStateEnum,
		BehaviorSubject<boolean>
	>([
		[this.orderStateEnum.OPEN, new BehaviorSubject<boolean>(false)],
		[this.orderStateEnum.CANCELED, new BehaviorSubject<boolean>(false)],
		[this.orderStateEnum.FILLED, new BehaviorSubject<boolean>(false)],
	])

	public isNeedUpdateOrderResponseMap: Map<OrderStateEnum, BehaviorSubject<boolean>> = new this.map<
		OrderStateEnum,
		BehaviorSubject<boolean>
	>([
		[this.orderStateEnum.OPEN, new BehaviorSubject<boolean>(false)],
		[this.orderStateEnum.CANCELED, new BehaviorSubject<boolean>(false)],
		[this.orderStateEnum.FILLED, new BehaviorSubject<boolean>(false)],
	])

	public ordersParamsMap: Map<OrderStateEnum, BehaviorSubject<OrderParamsInterface>> = new this.map<
		OrderStateEnum,
		BehaviorSubject<OrderParamsInterface>
	>([
		[
			this.orderStateEnum.OPEN,
			new BehaviorSubject<OrderParamsInterface>({
				status: this.orderStateEnum.OPEN,
				page: 1,
				pageSize: 30,
			}),
		],
		[
			this.orderStateEnum.CANCELED,
			new BehaviorSubject<OrderParamsInterface>({
				status: this.orderStateEnum.CANCELED,
				page: 1,
				pageSize: 30,
			}),
		],
		[
			this.orderStateEnum.FILLED,
			new BehaviorSubject<OrderParamsInterface>({
				status: this.orderStateEnum.FILLED,
				page: 1,
				pageSize: 30,
			}),
		],
	])

	private _calcFromPercent(percent: number, targetPrice: number, qty: number, factor: number): SlTpValuesInterface {
		const price: number = targetPrice + ((targetPrice * percent) / 100) * factor
		const dollar: number = ((targetPrice * percent) / 100) * qty
		return this._formatSlTp(percent, price, dollar)
	}

	private _calcFromPrice(price: number, targetPrice: number, qty: number, factor: number): SlTpValuesInterface {
		const percent: number = ((price - targetPrice) / targetPrice) * 100 * factor
		const dollar: number = (price - targetPrice) * qty * factor
		return this._formatSlTp(percent, price, dollar)
	}

	private _calcFromDollar(dollar: number, targetPrice: number, qty: number, factor: number): SlTpValuesInterface {
		const price: number = targetPrice + (dollar / qty) * factor
		const percent: number = ((price - targetPrice) / targetPrice) * 100 * factor
		return this._formatSlTp(percent, price, dollar)
	}

	private _formatSlTp(percent: number, price: number, dollar: number): SlTpValuesInterface {
		return {
			percent: parseFloat(percent.toFixed(2)),
			price: parseFloat(price.toFixed(price >= 1 ? 2 : 4)),
			dollar: parseFloat(dollar.toFixed(2)),
		}
	}

	private _checkCanEditParentOrder(order: OrderInterface | undefined): boolean {
		if (!order) return false
		if (order.order_class === this.orderClassEnum.OCO) return false
		return order.type !== this.orderTypeEnum.MARKET && [this.orderStatusEnum.NEW].includes(order.status)
	}

	private _checkCanEditChildrenOrders(order: OrderInterface | undefined): boolean {
		if (!order) return false
		const childOrders: OrderInterface[] = order.children_order || []
		const relevantChildOrders: OrderInterface[] = childOrders.filter((child: OrderInterface): boolean =>
			[this.orderAttributeEnum.STOP_LOSS, this.orderAttributeEnum.TAKE_PROFIT].includes(child.order_attribute),
		)
		return relevantChildOrders.some((child: OrderInterface): boolean =>
			[this.orderStatusEnum.NEW, this.orderStatusEnum.HELD].includes(child.status),
		)
	}

	private _updateOrdersResponse(
		ordersResponse: OrdersResponseInterface,
		updatedOrder: OrderInterface,
	): OrdersResponseInterface {
		const shouldReplace: boolean = ordersResponse.orders.some(
			(order: OrderInterface): boolean => order.id === updatedOrder.id || order.id === updatedOrder.replaces,
		)

		let updatedOrders: OrderInterface[] = []

		if (shouldReplace) {
			updatedOrders = ordersResponse.orders.filter((order: OrderInterface): boolean => {
				return !(order.id === updatedOrder.id || order.id === updatedOrder.replaces)
			})
			updatedOrders = [updatedOrder, ...updatedOrders]
		} else {
			updatedOrders = [updatedOrder, ...ordersResponse.orders]
		}

		return {
			...ordersResponse,
			orders: updatedOrders,
		}
	}

	private _getOrderState(order: OrderInterface): OrderStateEnum | null {
		const flatOrder: OrderInterface[] = [order, ...(order.children_order ?? [])]

		const isOpenedOrder: boolean = flatOrder.some((order: OrderInterface): boolean =>
			this.openedOrderStatuses.includes(order.status),
		)
		const isCanceledOrder: boolean = flatOrder.some((order: OrderInterface): boolean =>
			this.cancelOrderStatuses.includes(order.status),
		)
		const isFilledOrder: boolean = flatOrder.some((order: OrderInterface): boolean =>
			this.filledOrderStatuses.includes(order.status),
		)

		if (isOpenedOrder) return this.orderStateEnum.OPEN
		if (isCanceledOrder) return this.orderStateEnum.CANCELED
		if (isFilledOrder) return this.orderStateEnum.FILLED

		return null
	}

	public isSlTpActive(order: OrderInterface | null): boolean {
		return (
			!!order &&
			[this.orderStatusEnum.NEW, this.orderStatusEnum.HELD, this.orderStatusEnum.ACCEPTED].includes(order.status)
		)
	}

	public findStopLoss(order: OrderInterface | null): OrderInterface | null {
		if (!order) return null

		return (
			order.children_order?.find(
				(child: OrderInterface): boolean => child.order_attribute === this.orderAttributeEnum.STOP_LOSS,
			) || null
		)
	}

	public findTakeProfit(order: OrderInterface | null): OrderInterface | null {
		if (!order) return null

		if (order.order_class === this.orderClassEnum.OCO) return order

		return (
			order.children_order?.find(
				(child: OrderInterface): boolean => child.order_attribute === this.orderAttributeEnum.TAKE_PROFIT,
			) || null
		)
	}

	public calcSlTp(
		isBuy: boolean,
		qty: number,
		targetPrice: number,
		field: SlTpValuesEnum,
		value: number,
	): SlTpValuesInterface {
		const factor: FactorEnum = isBuy ? this.factorEnum.LONG : this.factorEnum.SHORT
		const calculations = {
			[this.slTpValuesEnum.PERCENT]: (): SlTpValuesInterface =>
				this._calcFromPercent(value, targetPrice, qty, factor),
			[this.slTpValuesEnum.PRICE]: (): SlTpValuesInterface =>
				this._calcFromPrice(value, targetPrice, qty, factor),
			[this.slTpValuesEnum.DOLLAR]: (): SlTpValuesInterface =>
				this._calcFromDollar(value, targetPrice, qty, factor),
		}
		return calculations[field]?.() ?? { percent: 0, price: 0, dollar: 0 }
	}

	public filterOrdersByPosition(position: PositionsInterface | null, orders: OrderInterface[]): OrderInterface[] {
		if (!position || !orders) return []
		const filteredOrders: OrderInterface[] = orders.filter(
			(order: OrderInterface): boolean => order.symbol === position.symbol,
		)

		const isOtoBracket: CallableFunction = (orderClass: OrderClassEnum): boolean =>
			[this.orderClassEnum.OTO, this.orderClassEnum.BRACKET].includes(orderClass)
		const isBuy: CallableFunction = (orderSide: OrderSideEnum): boolean => this.orderSideEnum.BUY === orderSide
		const isFilled: CallableFunction = (orderStatus: OrderStatusEnum): boolean =>
			this.orderStatusEnum.FILLED === orderStatus

		if (position.side === this.positionSideEnum.LONG) {
			return filteredOrders.filter((order: OrderInterface): boolean => {
				if (!isBuy(order.side)) return true

				return isFilled(order.status) && isOtoBracket(order.order_class)
			})
		}

		return filteredOrders.filter((order: OrderInterface): boolean => {
			if (isBuy(order.side)) return true

			return isFilled(order.status) && isOtoBracket(order.order_class)
		})
	}

	public checkCanEdit(order: OrderInterface | undefined): boolean {
		if (!order) return false
		return this._checkCanEditParentOrder(order) || this._checkCanEditChildrenOrders(order)
	}

	public checkCanCancel(order: OrderInterface | undefined): boolean {
		if (!order) return false

		const children: OrderInterface[] = order.children_order ?? []

		return !(
			order.status === this.orderStatusEnum.PENDING_CANCEL ||
			children.some((child: OrderInterface): boolean => child.status === this.orderStatusEnum.PENDING_CANCEL)
		)
	}

	public checkIsTrailingStopVisible(orderType: OrderTypeEnum | undefined): boolean {
		if (!orderType) return false
		return orderType === this.orderTypeEnum.TRAILING_STOP
	}

	public checkIsStopPriceVisible(orderType: OrderTypeEnum | undefined, orderClass?: OrderClassEnum): boolean {
		if (!orderType) return false
		if (orderClass && orderClass === this.orderClassEnum.OCO) return false
		return [this.orderTypeEnum.STOP_LIMIT, this.orderTypeEnum.STOP].includes(orderType)
	}

	public checkIsLimitPriceVisible(type: OrderTypeEnum | undefined, orderClass?: OrderClassEnum | undefined): boolean {
		if (!type) return false
		if (orderClass && orderClass === this.orderClassEnum.OCO) return false
		return [this.orderTypeEnum.STOP_LIMIT, this.orderTypeEnum.LIMIT].includes(type)
	}

	public checkIsComplexOrder(order: OrderInterface | null): boolean {
		if (!order) return false

		return this.orderClasses.includes(order.order_class)
	}

	public getOrderPrice(order: OrderInterface | null): number {
		if (!order) return 0

		let price: number = 0

		switch (order.type) {
			case this.orderTypeEnum.MARKET:
				price = +(order.price ?? 0)
				break

			case this.orderTypeEnum.STOP:
				price = +(order.stop_price ?? 0)
				break

			case this.orderTypeEnum.LIMIT:
			case this.orderTypeEnum.STOP_LIMIT:
				price = +(order.limit_price ?? 0)
				break

			case this.orderTypeEnum.TRAILING_STOP:
				price = +(order.stop_price ?? 0)
				break
		}

		return +(order.qty ?? 0) * price
	}

	public toggleLoadersOrders(state: OrderStateEnum, value: boolean): void {
		this.ordersLoadingMap.get(state)?.next(value && !this.ordersResponseMap.get(state)?.getValue())
	}

	public setOrdersResponseByState(ordersResponse: OrdersResponseInterface, state: OrderStateEnum): void {
		const ordersResponse$: BehaviorSubject<OrdersResponseInterface | null> | undefined =
			this.ordersResponseMap.get(state)
		if (!ordersResponse$) return

		ordersResponse$.next(ordersResponse)
	}

	public moveOrder(updatedOrder: OrderInterface): void {
		const orderState: OrderStateEnum | null = this._getOrderState(updatedOrder)
		if (!orderState) return

		const ordersResponse$: BehaviorSubject<OrdersResponseInterface | null> | undefined =
			this.ordersResponseMap.get(orderState)
		const orderParams: OrderParamsInterface | undefined = this.ordersParamsMap.get(orderState)?.getValue()
		let updatedOrdersResponse: OrdersResponseInterface | null = null

		if (orderState !== this.orderStateEnum.OPEN) this.removeOpenedOrder(updatedOrder)

		const ordersResponse: OrdersResponseInterface | null | undefined = ordersResponse$?.getValue()
		if (!ordersResponse || !orderParams) return

		if (orderParams.page !== 1) {
			this.isNeedUpdateOrderResponseMap.get(orderState)?.next(true)
			return
		} else {
			updatedOrdersResponse = this._updateOrdersResponse(ordersResponse, updatedOrder)
		}

		if (!ordersResponse$) return
		ordersResponse$.next(updatedOrdersResponse)
	}

	public removeOpenedOrder(removedOrder: OrderInterface): void {
		const openedOrdersResponse: OrdersResponseInterface | null | undefined = this.ordersResponseMap
			.get(this.orderStateEnum.OPEN)
			?.getValue()
		if (!openedOrdersResponse) return

		const updatedOrders: OrderInterface[] = openedOrdersResponse.orders.filter(
			(order: OrderInterface): boolean => order.id !== removedOrder.id,
		)

		this.ordersResponseMap.get(this.orderStateEnum.OPEN)?.next({
			...openedOrdersResponse,
			orders: updatedOrders,
		})
	}

	public formatOrderBySocket(order: OrderInterface): OrderInterface {
		return {
			...order,
			qty: +order.qty,
		}
	}

	public updateTrailOrdersStopPrice(trailOrders: TrailOrderSocketResponseInterface[]): void {
		const ordersResponse$: BehaviorSubject<OrdersResponseInterface | null> | undefined = this.ordersResponseMap.get(
			this.orderStateEnum.OPEN,
		)
		if (!ordersResponse$) return

		const ordersResponse: OrdersResponseInterface | null = ordersResponse$.getValue()
		if (!ordersResponse) return

		const updatedOrders: OrderInterface[] = ordersResponse.orders.map((order: OrderInterface): OrderInterface => {
			const trailOrder: TrailOrderSocketResponseInterface | undefined = trailOrders.find(
				(t: TrailOrderSocketResponseInterface): boolean => t.id === order.id,
			)
			if (!trailOrder) return order
			return { ...order, stop_price: +trailOrder.stop_price }
		})

		ordersResponse$.next({ ...ordersResponse, orders: updatedOrders })
	}
}
